import { useHandler } from "@redotech/react-util/hook";
import { useOptimizationSettings } from "@redotech/redo-merchant-app-common/support/use-optimization-settings";
import { ShopperAiSettingsOptimizationType } from "@redotech/redo-model/shopper-ai-settings-optimization";
import { toast } from "@redotech/redo-web/alert";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import {
  GenericSettingsOptimizationBanner,
  SettingToOptimize,
} from "../optimization-banners/generic-settings-optimization-banner";
import {
  checkBaseEligibility,
  OptimizationProps,
  SettingsOptimizationDefinition,
} from "../optimization-types";

const OrderEditingSkillsOptimizationBanner = memo(
  function OrderEditingSkillsOptimizationBanner({
    handleComplete,
    handleDismiss,
  }: OptimizationProps) {
    const { settings, mutation, handleApplyAll, isLoading } =
      useOptimizationSettings({
        applyAllRecommendations: {
          "orderEditing/tools/cancelOrder/enabled": true,
          "orderEditing/tools/cancelOrder/cancelOrderTimeWindowAfterPlacingMinutes":
            60 * 24,
          "orderEditing/tools/changeAddress/enabled": true,
          "orderEditing/tools/changeAddress/changeOrderAddressTimeWindowAfterPlacingMinutes":
            60 * 24,
          "orderEditing/tools/updateOrder/enabled": true,
          "orderEditing/tools/updateOrder/updateOrderTimeWindowAfterPlacingMinutes":
            60 * 24,
        },
        handleComplete,
        handleDismiss,
        shouldCompleteOnAnyChange: false,
      });

    const handleToggle = useHandler(
      async (
        key:
          | "orderEditing/tools/cancelOrder/enabled"
          | "orderEditing/tools/changeAddress/enabled"
          | "orderEditing/tools/updateOrder/enabled",
        value: boolean,
      ) => {
        if (!settings) {
          return;
        }
        await mutation.mutateAsync({ [key]: value });
        toast("Settings applied successfully", { variant: "success" });
      },
    );

    const handleTimeWindowUpdate = useHandler(
      async (
        key:
          | "orderEditing/tools/cancelOrder/cancelOrderTimeWindowAfterPlacingMinutes"
          | "orderEditing/tools/changeAddress/changeOrderAddressTimeWindowAfterPlacingMinutes"
          | "orderEditing/tools/updateOrder/updateOrderTimeWindowAfterPlacingMinutes",
        hours: string,
      ) => {
        if (!settings) {
          return;
        }
        const hoursNum = parseInt(hours, 10);
        if (isNaN(hoursNum) || hoursNum < 0) {
          return;
        }
        await mutation.mutateAsync({ [key]: hoursNum * 60 });
        toast("Settings applied successfully", { variant: "success" });
      },
    );

    if (isLoading || !settings) {
      return null;
    }

    const settingsConfig: SettingToOptimize[] = [
      {
        title: "Enable cancel order",
        description:
          "Allow AI to cancel orders after placement upon a customer request.",
        control: (
          <Flex align="center" gap="sm">
            <RedoToggle
              setValue={(value: boolean) =>
                handleToggle("orderEditing/tools/cancelOrder/enabled", value)
              }
              value={
                settings["orderEditing/tools/cancelOrder/enabled"] ?? false
              }
            />
            <Flex align="center" gap="xs">
              <Text fontSize="sm">Time window:</Text>
              <RedoTextInput
                inputTypeHint="number"
                max={72}
                min={1}
                setValue={(hours: string) =>
                  handleTimeWindowUpdate(
                    "orderEditing/tools/cancelOrder/cancelOrderTimeWindowAfterPlacingMinutes",
                    hours,
                  )
                }
                size="sm"
                step={1}
                value={String(
                  Math.round(
                    (settings[
                      "orderEditing/tools/cancelOrder/cancelOrderTimeWindowAfterPlacingMinutes"
                    ] ?? 1440) / 60,
                  ),
                )}
              />
              <Text fontSize="sm">hours</Text>
            </Flex>
          </Flex>
        ),
        onUpdate: async () => {
          await handleToggle(
            "orderEditing/tools/cancelOrder/enabled",
            settings["orderEditing/tools/cancelOrder/enabled"] ?? false,
          );
        },
      },
      {
        title: "Enable change shipping address",
        description:
          "Allow AI to update shipping addresses after placement upon a customer request.",
        control: (
          <Flex align="center" gap="sm">
            <RedoToggle
              setValue={(value: boolean) =>
                handleToggle("orderEditing/tools/changeAddress/enabled", value)
              }
              value={
                settings["orderEditing/tools/changeAddress/enabled"] ?? false
              }
            />
            <Flex align="center" gap="xs">
              <Text fontSize="sm">Time window:</Text>
              <RedoTextInput
                inputTypeHint="number"
                max={72}
                min={1}
                setValue={(hours: string) =>
                  handleTimeWindowUpdate(
                    "orderEditing/tools/changeAddress/changeOrderAddressTimeWindowAfterPlacingMinutes",
                    hours,
                  )
                }
                size="sm"
                step={1}
                value={String(
                  Math.round(
                    (settings[
                      "orderEditing/tools/changeAddress/changeOrderAddressTimeWindowAfterPlacingMinutes"
                    ] ?? 1440) / 60,
                  ),
                )}
              />
              <Text fontSize="sm">hours</Text>
            </Flex>
          </Flex>
        ),
        onUpdate: async () => {
          await handleToggle(
            "orderEditing/tools/changeAddress/enabled",
            settings["orderEditing/tools/changeAddress/enabled"] ?? false,
          );
        },
      },
      {
        title: "Enable update order",
        description:
          "Allow AI to modify order details after placement upon a customer request.",
        control: (
          <Flex align="center" gap="sm">
            <RedoToggle
              setValue={(value: boolean) =>
                handleToggle("orderEditing/tools/updateOrder/enabled", value)
              }
              value={
                settings["orderEditing/tools/updateOrder/enabled"] ?? false
              }
            />
            <Flex align="center" gap="xs">
              <Text fontSize="sm">Time window:</Text>
              <RedoTextInput
                inputTypeHint="number"
                max={72}
                min={1}
                setValue={(hours: string) =>
                  handleTimeWindowUpdate(
                    "orderEditing/tools/updateOrder/updateOrderTimeWindowAfterPlacingMinutes",
                    hours,
                  )
                }
                size="sm"
                step={1}
                value={String(
                  Math.round(
                    (settings[
                      "orderEditing/tools/updateOrder/updateOrderTimeWindowAfterPlacingMinutes"
                    ] ?? 1440) / 60,
                  ),
                )}
              />
              <Text fontSize="sm">hours</Text>
            </Flex>
          </Flex>
        ),
        onUpdate: async () => {
          await handleToggle(
            "orderEditing/tools/updateOrder/enabled",
            settings["orderEditing/tools/updateOrder/enabled"] ?? false,
          );
        },
      },
    ];

    return (
      <GenericSettingsOptimizationBanner
        handleComplete={handleComplete}
        handleDismiss={handleDismiss}
        onApplyAll={handleApplyAll}
        settings={settingsConfig}
      />
    );
  },
);

export const orderEditingSkillsOptimization: SettingsOptimizationDefinition = {
  type: ShopperAiSettingsOptimizationType.ORDER_EDITING_SKILLS_OPTIMIZATION,
  priority: 1,
  retries: 2,
  isEligible: async (team, user) => {
    if (!checkBaseEligibility(user)) {
      return false;
    }

    const cancelOrderEnabled =
      team.settings.orderEditing?.tools?.cancelOrder?.enabled ?? false;
    const changeAddressEnabled =
      team.settings.orderEditing?.tools?.changeAddress?.enabled ?? false;
    const updateOrderEnabled =
      team.settings.orderEditing?.tools?.updateOrder?.enabled ?? false;

    return (
      team.settings.concierge?.enabled === true &&
      (!cancelOrderEnabled || !changeAddressEnabled || !updateOrderEnabled)
    );
  },
  component: OrderEditingSkillsOptimizationBanner,
};
